'use client'

import Error from '@/components/common/error'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import DurationIcon from '@/components/icons/duration-icon'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { Skeleton } from '@/components/ui/skeleton'
import { isColorLight } from '@/helpers/color-utils'
import { localDateTime } from '@/helpers/datetime'
import useMediaQuery from '@/helpers/use-media-query'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import useEmblaCarousel from 'embla-carousel-react'
import { useEffect, useMemo, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import { getDisplaySessionsForSelectedSplits } from '../../utils'

export default function SessionCarousel({ event, selectedSplits, params }) {
  const [mounted, setMounted] = useState(false)

  const { mediaQueryResult } = useMediaQuery('(min-width: 1024px)')

  useEffect(() => {
    setMounted(true)
  }, [])

  const { data: sessions, isLoading: sessionsLoading } = useSWRImmutable(
    {
      url: `community/${params.communitySlug}/championship/${params.championshipSlug}/event/${params.eventId}/session`,
      searchParams: {
        include: {
          splits: {
            include: {
              championshipSplit: true,
            },
          },
        },
        orderBy: {
          targetDateTime: 'asc',
        },
      },
    },
    apiFetch,
  )

  const sessionsList = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions],
  )
  const championshipSplits = useMemo(
    () =>
      Array.isArray(event?.championship?.splits)
        ? event.championship.splits
        : [],
    [event?.championship?.splits],
  )
  const selectedSplitIds = useMemo(
    () => (Array.isArray(selectedSplits) ? selectedSplits : []),
    [selectedSplits],
  )

  const displaySessions = useMemo(() => {
    if (sessionsList.length === 0) return []
    if (championshipSplits.length === 0) return sessionsList

    return sessionsList.flatMap((session) =>
      getDisplaySessionsForSelectedSplits(session, selectedSplitIds),
    )
  }, [sessionsList, selectedSplitIds, championshipSplits])

  const emptySplitSessionsMessage =
    championshipSplits.length > 0
      ? selectedSplitIds.length > 0
        ? 'No sessions for selected splits'
        : 'Select at least one split to view sessions'
      : 'No sessions set for this event'

  return (
    <>
      <div className="screen-grid *:grid-indent">
        <div className="flex items-center gap-2 align-middle desktop-xs:gap-[15px]">
          <h3 className="mt-[-2px] font-inter text-h-5 font-semibold text-shades-0 desktop-xs:text-h-4">
            Sessions
          </h3>
          {sessions && (
            <p className="mt-[2px] text-p-1 font-semibold text-neutral-blue-300 desktop-xs:text-p-2">
              {sessionsList.length} Sessions
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-6 desktop-xs:mt-[22px]">
        {mounted && !sessionsLoading ? (
          sessionsList.length > 0 ? (
            mediaQueryResult ? (
              <DesktopCarousel
                sessions={displaySessions}
                emptyMessage={emptySplitSessionsMessage}
              />
            ) : (
              <MobileCarousel
                sessions={displaySessions}
                emptyMessage={emptySplitSessionsMessage}
              />
            )
          ) : (
            <div className="screen-grid *:grid-indent">
              <div className="mt-4 flex gap-8 desktop-xs:mt-[22px]">
                <div className="flex h-[131px] w-full items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 align-middle desktop-xs:h-[201px]">
                  <h5 className="m-auto text-center">
                    No sessions{' '}
                    {championshipSplits.length > 0
                      ? 'for selected splits.'
                      : 'in event.'}
                  </h5>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="screen-grid *:grid-indent">
            <Skeleton className="h-[201px] w-full rounded-md" />
          </div>
        )}
      </div>
    </>
  )
}

function MobileCarousel({ sessions, emptyMessage }) {
  const [carouselRef, _carouselControls] = useEmblaCarousel({})

  return (
    <div className="w-full overflow-hidden font-inter">
      {sessions?.length > 0 ? (
        <div className="racing-outer" ref={carouselRef}>
          <div className="carousel-container">
            {sessions?.map((session, index) => (
              <div
                key={session.id}
                className={`carousel-slide ${index === 0 ? 'flex-[0_0_calc(100%-24px)]! pl-[36px]!' : ''} ${index + 1 === sessions.length ? 'flex-[0_0_calc(100%-24px)]! pr-[24px]!' : ''}`}
              >
                <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-9 py-7 desktop-xs:gap-6">
                  <div className="flex items-center gap-3 align-middle desktop-xs:gap-3">
                    <div className="relative size-10 rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
                      <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 capitalize">
                        {session.type[0]}
                      </p>
                    </div>
                    <p className="text-p-2 font-semibold capitalize text-neutral-blue-200">
                      {session.type}
                    </p>
                  </div>
                  <h6 className="text-h-6 font-semibold text-shades-0">
                    {!session.targetDateTime || !session.targetTimezone
                      ? 'N/A'
                      : localDateTime({
                          datetime: session.targetDateTime,
                          timezone: session.targetTimezone,
                          format: 'ddd D MMM • HH:mm z',
                        })}
                  </h6>
                  <div className="flex gap-2">
                    <DurationIcon className="size-6 stroke-[2px] pb-px" />
                    <p className="text-p-2 font-semibold text-shades-0">
                      {session.duration} {session.durationType}
                      {session.duration > 0 ? 's' : ''}
                    </p>
                  </div>

                  {session.splits.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {session.splits.map((split, index) => (
                        <div
                          key={index}
                          className="rounded-full px-4 py-1"
                          style={{
                            backgroundColor: `${
                              split.championshipSplit.color
                                ? split.championshipSplit.color
                                : split.championshipSplit.priority === 0
                                  ? '#FF9900'
                                  : split.championshipSplit.priority === 1
                                    ? '#F2F2F2'
                                    : split.championshipSplit.priority === 2
                                      ? '#FF7222'
                                      : 'white'
                            }`,
                          }}
                        >
                          <p
                            className={cn('', {
                              'text-black': isColorLight(
                                split.championshipSplit.color,
                              ),
                              'text-shades-0': !isColorLight(
                                split.championshipSplit.color,
                              ),
                            })}
                          >
                            {split.championshipSplit.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : sessions?.length === 0 ? (
        <div className="mx-[24px] flex w-[calc(100%-48px)] items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 align-middle">
          <p className="mx-auto w-fit px-4 py-10 text-p-1 desktop-xs:text-p-2">
            {emptyMessage}
          </p>
        </div>
      ) : (
        <Error
          icon="exclamation"
          size="hero"
          className="mx-[24px] w-[calc(100%-48px)] bg-neutral-blue-800 text-white"
          error={{
            code: 404,
            message: `Unable to load sessions.`,
          }}
          skipConsole={true}
        />
      )}
    </div>
  )
}

function DesktopCarousel({ sessions, emptyMessage }) {
  const [carouselController, setCarouselController] = useState(null)
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!carouselController) {
      return
    }

    setCount(carouselController.scrollSnapList().length)

    carouselController.on('select', () => {
      setCurrent(carouselController.selectedScrollSnap() + 1)
    })
  }, [carouselController])

  return (
    <div className="screen-grid relative">
      {/* left button */}
      <div className="absolute left-2 flex h-full w-fit flex-col justify-center align-middle desktop-sm:relative desktop-sm:left-0 desktop-sm:h-auto desktop-sm:w-full">
        {current > 0 && (
          <div
            className="carousel-btn-bg group flex h-full cursor-pointer select-none flex-col justify-center rounded-sm bg-neutral-blue-900 align-middle transition-all"
            onClick={() => {
              carouselController?.scrollPrev()
              setCurrent((prev) => prev - 1)
            }}
          >
            <div className="mx-auto flex size-8 cursor-pointer items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 align-middle group-hover:bg-neutral-blue-600 desktop-sm:size-12">
              <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
            </div>
          </div>
        )}
      </div>
      {/* carousel */}
      <div className="col-span-10 flex flex-1 overflow-hidden">
        {sessions?.length > 0 ? (
          <Carousel
            setApi={setCarouselController}
            opts={{ slidesToScroll: 2 }}
            className="w-full"
          >
            <CarouselContent>
              {sessions.map((session, index) => (
                <CarouselItem key={session.id} className="basis-1/4">
                  <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-9 py-7 desktop-xs:gap-6">
                    <div className="flex items-center gap-2 align-middle desktop-xs:gap-3">
                      <div className="relative size-10 rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
                        <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 capitalize">
                          {session.type[0]}
                        </p>
                      </div>
                      <p className="text-p-2 font-semibold capitalize text-neutral-blue-200">
                        {session.type}
                      </p>
                    </div>
                    <h6 className="text-h-6 font-semibold text-shades-0">
                      {!session.targetDateTime || !session.targetTimezone
                        ? 'N/A'
                        : localDateTime({
                            datetime: session.targetDateTime,
                            timezone: session.targetTimezone,
                            format: 'ddd D MMM • HH:mm z',
                          })}
                    </h6>
                    <div className="flex gap-2">
                      <DurationIcon className="size-6 stroke-[2px] pb-px" />
                      <p className="text-p-2 font-semibold text-shades-0">
                        {session.duration} {session.durationType}
                        {session.duration > 1 ? 's' : ''}
                      </p>
                    </div>
                    {session.splits.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        {session.splits.map((split, index) => (
                          <div
                            key={index}
                            className="rounded-full px-4 py-1"
                            style={{
                              backgroundColor: `${
                                split.championshipSplit.color
                                  ? split.championshipSplit.color
                                  : split.championshipSplit.priority === 0
                                    ? '#FF9900'
                                    : split.championshipSplit.priority === 1
                                      ? '#F2F2F2'
                                      : split.championshipSplit.priority === 2
                                        ? '#FF7222'
                                        : 'white'
                              }`,
                            }}
                          >
                            <p
                              className={cn('', {
                                'text-black': isColorLight(
                                  split.championshipSplit.color,
                                ),
                                'text-shades-0': !isColorLight(
                                  split.championshipSplit.color,
                                ),
                              })}
                            >
                              {split.championshipSplit.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : sessions?.length === 0 ? (
          <div className="flex w-full items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 align-middle">
            <p className="mx-auto w-fit px-4 py-10 text-p-1 desktop-xs:text-p-2">
              {emptyMessage}
            </p>
          </div>
        ) : (
          <Error
            icon="exclamation"
            size="hero"
            className="bg-neutral-blue-800! mx-auto w-full max-w-7xl text-white"
            error={{
              code: 404,
              message: `Unable to load sessions.`,
            }}
            skipConsole={true}
          />
        )}
      </div>
      {/* right button */}
      {sessions?.length > 4 && (
        <div className="absolute right-2 flex h-full w-fit flex-col justify-center align-middle desktop-sm:relative desktop-sm:right-0 desktop-sm:h-auto desktop-sm:w-full">
          {current <= count && (
            <div
              className="carousel-btn-bg group flex h-full cursor-pointer select-none flex-col justify-center rounded-sm bg-neutral-blue-900 align-middle transition-all"
              onClick={() => {
                carouselController?.scrollNext()
                setCurrent((prev) => prev + 1)
              }}
            >
              <div className="mx-auto flex size-8 cursor-pointer items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 align-middle group-hover:bg-neutral-blue-600 desktop-sm:size-12">
                <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
